import { useState, useEffect } from "react";
export function useMediaQuery(query) {
    const [matches, setMatches] = useState(false);
    useEffect(() => {
        const mediaQuery = window.matchMedia(query);
        setMatches(mediaQuery.matches);
        const handler = (event) => {
            setMatches(event.matches);
        };
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", handler);
            return () => mediaQuery.removeEventListener("change", handler);
        }
        else {
            mediaQuery.addListener(handler);
            return () => mediaQuery.removeListener(handler);
        }
    }, [query]);
    return matches;
}
export function useBreakpoint(theme) {
    const isWide = useMediaQuery(`(min-width: ${theme.breakpoints.wide})`);
    const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.desktop})`);
    const isTablet = useMediaQuery(`(min-width: ${theme.breakpoints.tablet})`);
    if (isWide)
        return "wide";
    if (isDesktop)
        return "desktop";
    if (isTablet)
        return "tablet";
    return "mobile";
}
export function useIsMobile(theme) {
    return useMediaQuery(`(max-width: ${parseInt(theme.breakpoints.tablet) - 1}px)`);
}
export function useIsTablet(theme) {
    const minWidth = theme.breakpoints.tablet;
    const maxWidth = `${parseInt(theme.breakpoints.desktop) - 1}px`;
    return useMediaQuery(`(min-width: ${minWidth}) and (max-width: ${maxWidth})`);
}
export function useIsDesktop(theme) {
    return useMediaQuery(`(min-width: ${theme.breakpoints.desktop})`);
}
export function useResponsiveValue(theme, values) {
    const breakpoint = useBreakpoint(theme);
    if (values[breakpoint] !== undefined) {
        return values[breakpoint];
    }
    if (breakpoint === "wide" && values.desktop !== undefined) {
        return values.desktop;
    }
    if ((breakpoint === "desktop" || breakpoint === "wide") &&
        values.tablet !== undefined) {
        return values.tablet;
    }
    if (values.mobile !== undefined) {
        return values.mobile;
    }
    return undefined;
}
export function responsiveStyles(styles, currentBreakpoint) {
    const baseStyles = styles.base || {};
    const breakpointStyles = styles[currentBreakpoint] || {};
    return {
        ...baseStyles,
        ...breakpointStyles,
    };
}
export function getResponsiveMaxWidth(theme, breakpoint, customMaxWidths) {
    if (customMaxWidths?.[breakpoint]) {
        return customMaxWidths[breakpoint];
    }
    const defaults = {
        mobile: "100%",
        tablet: "100%",
        desktop: "840px",
        wide: "1200px",
    };
    return defaults[breakpoint];
}
export function getResponsivePadding(theme, breakpoint) {
    const paddingMap = {
        mobile: "md",
        tablet: "lg",
        desktop: "xl",
        wide: "2xl",
    };
    return theme.spacing[paddingMap[breakpoint]];
}
export function getResponsiveFontSize(theme, breakpoint, size = "fontSizeMd") {
    if (breakpoint === "mobile" && size.startsWith("fontSize")) {
        const sizeMap = {
            fontSize3xl: "fontSize2xl",
            fontSize2xl: "fontSizeXl",
            fontSizeXl: "fontSizeLg",
            fontSizeLg: "fontSizeMd",
        };
        const adjustedSize = sizeMap[size] || size;
        return theme.typography[adjustedSize];
    }
    return theme.typography[size];
}
export function isTouchDevice() {
    return ("ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0);
}
export const MIN_TOUCH_TARGET_SIZE = "44px";
export function getResponsiveInputHeight(breakpoint) {
    return breakpoint === "mobile" && isTouchDevice()
        ? MIN_TOUCH_TARGET_SIZE
        : "40px";
}
